import type { ReactNode } from 'react';
import type { App } from '@hangar/shared';
import { formatDate } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { RowActions, type RowAction } from '@/components/RowActions';
import { FRAMEWORK_ICON, FRAMEWORK_LABEL, FrameworkBadge, Tag } from '@/components/badges';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AppPageLink } from './AppPageLink';
import { useSeesProviders } from '@/lib/useAudience';
import { buildSetupText } from './appsTableModel';
import { frameworkLabelClass, lastBuildSuffix, providerTags, type ProviderTag } from './appsSectionModel';

interface Props {
  app: App;
  projectSlug: string;
  index: number;
  selected: boolean;
  onSelect: () => void;
  actions: RowAction[];
}

const CARD_CLASS = '@container lift relative h-full gap-3 bg-secondary/40 p-4 shadow-none transition-colors has-[button[aria-pressed]:focus-visible]:ring-[3px] has-[button[aria-pressed]:focus-visible]:ring-ring/50';

function FrameworkGlyph({ app }: { app: App }) {
  return (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border bg-card text-muted-foreground">
      <Icon name={FRAMEWORK_ICON[app.framework]} size={22} className={app.framework === 'android_native' ? 'text-android' : undefined} />
    </span>
  );
}

function AppCardHeader({ app, projectSlug, selected, onSelect }: Pick<Props, 'app' | 'projectSlug' | 'selected' | 'onSelect'>) {
  return (
    <div className="flex items-start gap-3">
      <FrameworkGlyph app={app} />
      <div className="min-w-0 flex-1">
        <button type="button" onClick={onSelect} aria-pressed={selected} className="block w-full truncate rounded-lg text-left text-[15px] font-semibold text-foreground outline-none after:absolute after:inset-0 after:rounded-lg after:content-['']">
          {app.name}
        </button>
        <div className="truncate font-mono text-xs text-subtle">{app.slug}</div>
      </div>
      {/* The card selects the app on this page; the arrow (above the stretched button) opens its App page. */}
      <AppPageLink app={app} projectSlug={projectSlug} testId={`app-open-${app.slug}`} className="relative z-10 -my-0.5" />
      {/* A long framework label ("Android native") keeps only its glyph in narrow cards, so the name and the slug keep their room. */}
      <FrameworkBadge framework={app.framework} labelClassName={frameworkLabelClass(FRAMEWORK_LABEL[app.framework])} />
    </div>
  );
}

/** A term/description pair of the card's details list. */
function Detail({ term, value, title }: { term: string; value: string; title?: string }) {
  return (
    <>
      <dt className="text-subtle">{term}</dt>
      <dd className="truncate font-mono text-muted-foreground" title={title}>
        {value}
      </dd>
    </>
  );
}

/** The working directory; operators also get the provider ids (SPEC decision 23). */
function AppCardDetails({ app }: { app: App }) {
  const operator = useSeesProviders();
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
      <Detail term="Working dir" value={app.workingDirectory} />
      {/* hangar.yml sync is after the MVP (docs/mvp-plan.md P9): its path is not shown. */}
      {operator && app.easProjectId ? <Detail term="EAS project" value={app.easProjectId} title={app.easProjectId} /> : null}
      {operator && app.bitriseAppSlug ? <Detail term="Bitrise app" value={app.bitriseAppSlug} /> : null}
      {operator ? null : <Detail term="Build setup" value={buildSetupText(app)} />}
    </dl>
  );
}

const PROVIDER_TAG: Record<ProviderTag, () => ReactNode> = {
  eas: () => <Tag key="eas">EAS</Tag>,
  bitrise: () => (
    <Tag key="bitrise" className="text-bitrise">
      Bitrise
    </Tag>
  ),
  none: () => (
    <Tag key="none" title="No build provider linked yet">
      no provider
    </Tag>
  ),
};

function AppCardFooter({ app, actions }: Pick<Props, 'app' | 'actions'>) {
  const operator = useSeesProviders();
  return (
    <div className="mt-auto flex items-center justify-between gap-2 border-t pt-3 font-mono text-xs text-subtle">
      <span>
        created {formatDate(app.createdAt)}
        {lastBuildSuffix(app)}
      </span>
      <span className="flex items-center gap-1">
        {operator ? providerTags(app).map((tag) => PROVIDER_TAG[tag]()) : null}
        {actions.length ? <RowActions actions={actions} subject={app.name} variant="menu" testId={`app-actions-${app.slug}`} className="relative z-10 -my-2 -mr-2" /> : null}
      </span>
    </div>
  );
}

export function AppCard({ app, projectSlug, index, selected, onSelect, actions }: Props) {
  return (
    <li data-testid={`app-row-${app.slug}`} data-selected={selected} style={stagger(index)} className="enter">
      {/* The name is the select button, stretched over the card (after:inset-0); the actions menu sits above it. */}
      <Card className={cn(CARD_CLASS, selected && 'border-primary/50 bg-primary/5')}>
        <AppCardHeader app={app} projectSlug={projectSlug} selected={selected} onSelect={onSelect} />
        <AppCardDetails app={app} />
        <AppCardFooter app={app} actions={actions} />
      </Card>
    </li>
  );
}

export function AppCardSkeleton() {
  return (
    <Card className="gap-3 p-4 shadow-none">
      <div className="flex items-start gap-3">
        <Skeleton className="size-10 rounded-lg" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-5 w-16" />
      </div>
      <CardSkeletonBody />
    </Card>
  );
}

function CardSkeletonBody() {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-3 w-48" />
      </div>
      <div className="flex items-center justify-between border-t pt-3">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-4 w-10" />
      </div>
    </>
  );
}
